
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';
import { TranslationText } from '@/components/i18n/TranslationText';

export function SeedTranslations() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSeedTranslations = async () => {
    setIsLoading(true);
    
    try {
      // Call the Edge Function to seed the translations
      // We'll use the default headers automatically applied by the invoke method
      // This avoids accessing the protected supabaseKey property
      const { data, error } = await supabase.functions.invoke('seed-translations');
      
      if (error) {
        throw new Error(`Error invoking seed-translations function: ${error.message}`);
      }
      
      // Reload translations from database to update the UI
      if (i18n.reloadResourcesFromDb) {
        await i18n.reloadResourcesFromDb();
      } else {
        await i18n.reloadResources();
      }
      
      toast({
        title: t('success'),
        description: t('translationsSeeded'),
      });
      
      // Force UI update by changing language and back
      const currentLang = i18n.language;
      await i18n.changeLanguage('temp-trigger-reload');
      await i18n.changeLanguage(currentLang);
      
    } catch (error) {
      console.error('Error seeding translations:', error);
      toast({
        title: t('error'),
        description: 'Failed to seed translations. See console for details.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <TranslationText i18nKey="seedTranslations">
            Seed Translations
          </TranslationText>
        </CardTitle>
        <CardDescription>
          <TranslationText i18nKey="seedTranslationsFromDefaults">
            Initialize the database with default translations for all supported languages.
          </TranslationText>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-4">
          <TranslationText i18nKey="seedTranslationsDescription">
            This will add default translation keys and values to the database for English, Spanish, and Norwegian.
            Any existing translations with the same keys will be preserved.
          </TranslationText>
        </p>
        <Button
          onClick={handleSeedTranslations}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <TranslationText i18nKey="seedingTranslations">
                Seeding Translations...
              </TranslationText>
            </>
          ) : (
            <TranslationText i18nKey="seedTranslationsFromDefaults">
              Seed Default Translations
            </TranslationText>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
